import { api, ApiError, WEB_VERSION, type Account, type Me } from '../sync/api';
import { thDateTime } from '../time';
import { $ } from '../ui/dom';

/*
 * Nothing in the app opens until the person is signed in with an approved account.
 *   not signed in       → sign-in page ("เข้าสู่ระบบด้วย Google")
 *   waiting / disabled  → status page with "ตรวจสถานะอีกครั้ง" and "ออกจากระบบ"
 *   no connection       → if this device was last used by an approved account, open offline for that
 *                         account (work saves on this device and goes up once back online);
 *                         otherwise the sign-in page with the reason and "ลองอีกครั้ง"
 */

const REMEMBER = 'cs_account';

function remember(a: Account) {
  try { localStorage.setItem(REMEMBER, JSON.stringify({ id: a.id, email: a.email, name: a.name, role: a.role, status: a.status })); } catch { /* private mode: just not remembered */ }
}
export function forgetAccount() {
  try { localStorage.removeItem(REMEMBER); } catch { /* nothing to remove */ }
}
function remembered(): Account | null {
  try {
    const a = JSON.parse(localStorage.getItem(REMEMBER) ?? 'null') as Account | null;
    return a && typeof a.id === 'string' && a.status === 'active' ? a : null;
  } catch { return null; }
}

export interface Entry { account: Account; me: Me | null; offlineReason?: string }

const gate = () => $('gate');
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};
const errMsg = (e: unknown) => e instanceof Error ? e.message : String(e);

function shell(): HTMLElement {
  const g = gate();
  g.textContent = '';
  g.hidden = false;
  const card = el('div', 'gate-card');
  g.append(card);
  return card;
}

function logo() {
  const wrap = el('div', 'gate-brand');
  const mark = el('div', 'gate-logo');
  const img = document.createElement('img');
  img.src = '/icons/logo-mark.png'; img.alt = ''; img.width = 88; img.height = 88;
  mark.append(img);
  wrap.append(mark, el('h1', '', 'CraftStudio'), el('p', '', 'ออกแบบโพสต์ โปสเตอร์ ฉลาก และงานส่งโรงพิมพ์ของกลุ่ม'));
  return wrap;
}

function versionLine(server: string | null) {
  const p = el('p', 'gate-ver');
  if (server == null) p.textContent = `เวอร์ชันหน้าเว็บ ${WEB_VERSION} · เวลาประเทศไทย`;
  else if (server === WEB_VERSION) p.textContent = `เวอร์ชัน ${WEB_VERSION} · เวลาประเทศไทย`;
  else { p.textContent = `⚠ หน้าเว็บ ${WEB_VERSION} · เซิร์ฟเวอร์ ${server} ไม่ตรงกัน ลองเปิดหน้าใหม่`; p.classList.add('bad'); }
  // Google checks that the sign-in page links to the privacy policy (public/privacy.html)
  const wrap = el('div', 'gate-foot');
  const a = el('a', '', 'นโยบายความเป็นส่วนตัว');
  a.href = '/privacy';
  wrap.append(p, a);
  return wrap;
}

function checking() {
  const card = shell();
  card.append(logo(), el('p', 'gate-note', 'กำลังตรวจการเข้าสู่ระบบ…'));
}

/** Sign-in page. Resolves when the person taps "ลองอีกครั้ง" (signing in itself leaves the page). */
async function showLogin(error: string | null): Promise<void> {
  checking();
  let ready = false, notReady: string | null = null, server: string | null = null;
  try {
    const st = await api.authStatus();
    ready = st.ready; notReady = st.reason; server = st.version;
  } catch (e) {
    notReady = 'ตรวจสถานะระบบเข้าสู่ระบบไม่ได้: ' + errMsg(e);
  }
  return new Promise(resolve => {
    const card = shell();
    const box = el('section', 'gate-box');
    box.append(el('h2', '', 'เข้าสู่ระบบ'), el('p', '', 'ใช้บัญชี Google (Gmail) ของคุณ ครั้งแรกต้องรอผู้ดูแลอนุมัติก่อนจึงจะใช้งานได้'));
    if (ready) {
      const a = el('a', 'gbtn');
      a.href = '/api/auth/google/start';
      a.innerHTML = '<span class="g" aria-hidden="true">G</span>';
      a.append('เข้าสู่ระบบด้วย Google');
      box.append(a);
    } else {
      const b = el('button', 'gbtn', 'เข้าสู่ระบบด้วย Google') as HTMLButtonElement;
      b.disabled = true;
      box.append(b);
    }
    for (const msg of [error, ready ? null : notReady]) {
      if (msg) { const p = el('p', 'gate-err', msg); p.setAttribute('role', 'alert'); box.append(p); }
    }
    if (error || !ready) {
      const retry = el('button', 'btn', 'ลองอีกครั้ง') as HTMLButtonElement;
      retry.onclick = () => resolve();
      box.append(retry);
    }
    if (ready && (location.hostname === 'localhost' || location.hostname === '127.0.0.1')) box.append(devForm());
    card.append(logo(), box, versionLine(server));
  });
}

/** Local development only (the server refuses it anywhere else): sign in as any email. */
function devForm() {
  const f = el('form', 'gate-dev');
  f.method = 'get'; f.action = '/api/auth/dev';
  const label = el('label', '', 'ทดสอบในเครื่อง: เข้าสู่ระบบเป็นอีเมล');
  label.htmlFor = 'devEmail';
  const input = el('input') as HTMLInputElement;
  input.type = 'email'; input.name = 'email'; input.id = 'devEmail'; input.autocomplete = 'off'; input.required = true;
  f.append(label, input, el('button', 'btn', 'เข้า'));
  return f;
}

/** Waiting for approval, or disabled. Resolves on "ตรวจสถานะอีกครั้ง" or after signing out. */
function showStatus(me: Me): Promise<void> {
  return new Promise(resolve => {
    const card = shell();
    const u = me.user;
    const box = el('section', 'gate-box center');
    const pending = u.status === 'pending';
    const icon = el('div', 'gate-icon');
    icon.innerHTML = pending
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></svg>';
    box.append(icon, el('h2', '', pending ? 'รอผู้ดูแลอนุมัติ' : 'บัญชีนี้ถูกปิดการใช้งาน'));
    box.append(el('p', '', pending
      ? 'ส่งคำขอใช้งานแล้ว เมื่อผู้ดูแลอนุมัติ กดตรวจอีกครั้งหรือเปิดแอปใหม่ได้เลย'
      : `ผู้ดูแลปิดการใช้งานเมื่อ ${u.statusAt ? thDateTime(new Date(u.statusAt)) : '-'} ติดต่อผู้ดูแลกลุ่มเพื่อขอเปิดใหม่ งานที่สร้างไว้ยังอยู่ครบ`));
    const facts = el('dl', 'gate-facts');
    const fact = (k: string, v: string) => { facts.append(el('dt', '', k), el('dd', '', v)); };
    fact('บัญชี', u.email);
    fact('ส่งคำขอเมื่อ', thDateTime(new Date(u.createdAt)));
    fact('สถานะ', pending ? 'รออนุมัติ' : 'ปิดการใช้งาน');
    box.append(facts);
    const msg = el('p', 'gate-err');
    msg.hidden = true;
    const again = el('button', 'btn main big', 'ตรวจสถานะอีกครั้ง') as HTMLButtonElement;
    again.onclick = () => resolve();
    const out = el('button', 'btn big', 'ออกจากระบบ / ใช้บัญชีอื่น') as HTMLButtonElement;
    out.onclick = async () => {
      out.disabled = true;
      try { await api.logout(); forgetAccount(); resolve(); }
      catch (e) { msg.textContent = 'ออกจากระบบไม่สำเร็จ: ' + errMsg(e); msg.hidden = false; out.disabled = false; }
    };
    box.append(msg, again, out);
    card.append(logo(), box, versionLine(me.version));
  });
}

/** Resolves only with an approved account (or the remembered one when offline). */
export async function enter(): Promise<Entry> {
  const params = new URLSearchParams(location.search);
  let loginError = params.get('login_error');
  if (loginError) { params.delete('login_error'); history.replaceState(null, '', location.pathname + (params.toString() ? `?${params}` : '') + location.hash); }

  for (;;) {
    checking();
    try {
      const me = await api.me();
      if (me.user.status === 'active') {
        remember(me.user);
        gate().hidden = true;
        return { account: me.user, me };
      }
      forgetAccount();
      await showStatus(me);
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 0) {
        const a = remembered();
        if (a) { gate().hidden = true; return { account: a, me: null, offlineReason: errMsg(e) }; }
        await showLogin('ติดต่อเซิร์ฟเวอร์ไม่ได้ จึงตรวจการเข้าสู่ระบบไม่ได้: ' + errMsg(e));
      } else if (status === 401 || status === 503) {
        if (status === 401) forgetAccount();
        await showLogin(loginError);
      } else {
        await showLogin('ตรวจการเข้าสู่ระบบไม่สำเร็จ: ' + errMsg(e));
      }
      loginError = null;
    }
  }
}

/* ---------- signed out while working ---------- */

/** A bar over the work: the edits stay on this device; signing in again continues the upload. */
export function showRelogin(reason: string, beforeLeave: () => Promise<void>) {
  const bar = $('relogin');
  $('reloginMsg').textContent = `${reason} งานที่แก้ยังเก็บอยู่ในเครื่องนี้`;
  bar.hidden = false;
  ($('reloginBtn') as HTMLButtonElement).onclick = async () => {
    await beforeLeave();
    location.href = '/api/auth/google/start';
  };
}
